/**
 * pagesByCategory — the editor sidebar's section-grouping derivation, extracted
 * pure (Story 37.9, Task 4 — AC #4).
 *
 * Previously an inline `useMemo` inside `$reportId.edit.tsx`. Mounting the full
 * editor route in jsdom is impractical (it pulls in many providers — router,
 * query, DnD, date-range), so per Task 4 the derivation is extracted into a tiny
 * DOM-free leaf and unit-tested directly; the route delegates to it
 * (behavior-preserving — AC #8). The ⋮-menu actions and CTAs (which need the
 * wired route) are covered by e2e.
 *
 * The rule (verbatim from the route): a section is SHOWN iff it has pages, OR is
 * in `activeSections` (an explicitly-created empty section), OR is the
 * `defaultCategory` (always shown as a structural anchor so the sidebar never
 * reads as an empty void). Then the user's custom `sectionOrder` reorders the
 * visible sections: ids in `sectionOrder` come first (in that order); any
 * visible section not yet listed keeps its natural PAGE_CATEGORIES position.
 */

/** Minimal page shape this derivation reads (the only field it touches). */
export interface CategorizablePage {
  category?: string;
}

/** Minimal category shape (id + default label). */
export interface SectionCategory {
  id: string;
  label: string;
}

/** A grouped, display-ready section. */
export interface CategorizedSection<P extends CategorizablePage> {
  categoryId: string;
  /** Effective label = custom override if set, else the default category label. */
  category: string;
  pages: P[];
}

/**
 * Group pages into display-ready sections, applying the show-iff filter and the
 * custom section order.
 *
 * @param sortedPages   - pages already sorted by `order` (the route passes the sorted list).
 * @param categories    - PAGE_CATEGORIES (provides the natural order + default labels).
 * @param defaultCategory - DEFAULT_CATEGORY id (always shown).
 * @param sectionLabels - per-category custom label overrides.
 * @param activeSections - explicitly-created empty sections (shown even with 0 pages).
 * @param sectionOrder  - the user's custom display order of category ids.
 */
export function buildPagesByCategory<P extends CategorizablePage>(
  sortedPages: P[],
  categories: readonly SectionCategory[],
  defaultCategory: string,
  sectionLabels: Record<string, string>,
  activeSections: string[],
  sectionOrder: string[],
): Array<CategorizedSection<P>> {
  // Resolve each page's BUCKET id up-front: absent OR unknown (not a known
  // category id) coalesces to the default section. Without this, a page whose
  // `category` is a non-empty string that isn't in `categories` (e.g. a
  // hand-edited / corrupt layoutJson, or a category removed in a future release
  // while old saved reports still reference it) would match NO bucket and be
  // silently dropped from the sidebar → invisible + unreachable (code-review R1).
  // This mirrors the deliberate empty-string→default coercion upstream
  // (report-migration.ts sanitizePage) so the two stay symmetric.
  const knownIds = new Set(categories.map((c) => c.id));
  const bucketOf = (p: P): string => {
    const c = p.category;
    return c != null && knownIds.has(c) ? c : defaultCategory;
  };

  const visible = categories
    .map((cat) => ({
      categoryId: cat.id,
      category: sectionLabels[cat.id] ?? cat.label,
      pages: sortedPages.filter((p) => bucketOf(p) === cat.id),
    }))
    .filter(
      (g) =>
        g.pages.length > 0 ||
        activeSections.includes(g.categoryId) ||
        g.categoryId === defaultCategory,
    );

  // Code-review R2/R3: the no-drop guarantee above relies on `defaultCategory`
  // being a member of `categories` (so a row exists to receive the coalesced
  // pages). In production that always holds (DEFAULT_CATEGORY = PAGE_CATEGORIES[0]),
  // but a caller passing a default id NOT in `categories` would still lose every
  // coalesced page. Guard it: surface a synthetic default section so the pages
  // stay reachable. R3: also surface it when the default is active-but-empty, so
  // the "default is always shown as a structural anchor" rule holds on this
  // defensive path too (consistent with the in-categories case).
  if (!knownIds.has(defaultCategory)) {
    const orphaned = sortedPages.filter((p) => bucketOf(p) === defaultCategory);
    if (orphaned.length > 0 || activeSections.includes(defaultCategory)) {
      visible.push({
        categoryId: defaultCategory,
        category: sectionLabels[defaultCategory] ?? defaultCategory,
        pages: orphaned,
      });
    }
  }

  // Apply the user's custom section order: ids in sectionOrder come first (in
  // that order); any visible section not yet listed keeps its natural position.
  if (sectionOrder.length === 0) return visible;
  const rank = new Map(sectionOrder.map((id, i) => [id, i]));
  return [...visible].sort((a, b) => {
    const ra = rank.has(a.categoryId) ? rank.get(a.categoryId)! : Infinity;
    const rb = rank.has(b.categoryId) ? rank.get(b.categoryId)! : Infinity;
    return ra - rb;
  });
}
